<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const rawConnectionStatus = ref('');
const qrCode = ref(null);
const pairCode = ref('');
const isGeneratingQr = ref(false);
const isDisconnecting = ref(false);

const normalizeStatus = status => {
  if (status === undefined || status === null) {
    return '';
  }
  return status.toString().trim().toLowerCase();
};

const isConnectedStatus = status => normalizeStatus(status) === 'connected';

const isConnected = computed(() =>
  isConnectedStatus(rawConnectionStatus.value)
);

const statusText = computed(() => {
  if (isConnected.value) return t('INBOX_MGMT.ADD.UAZAPI.STATUS.CONNECTED');
  const status = normalizeStatus(rawConnectionStatus.value);
  if (['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(status)) {
    return t('INBOX_MGMT.ADD.UAZAPI.STATUS.CONNECTING');
  }
  return t('INBOX_MGMT.ADD.UAZAPI.STATUS.DISCONNECTED');
});

const toDataUrl = async value => {
  if (!value) return null;

  const trimmed = value.toString().trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('data:image')) {
    return trimmed;
  }

  const compact = trimmed.replace(/\s+/g, '');

  const isBase64 = () => {
    try {
      return btoa(atob(compact)) === compact;
    } catch (error) {
      return false;
    }
  };

  if (isBase64()) {
    return `data:image/png;base64,${compact}`;
  }

  return QRCode.toDataURL(trimmed);
};

const normalizeQrPayload = async payload => {
  if (!payload) return null;

  if (typeof payload === 'string') {
    return toDataUrl(payload);
  }

  if (payload && typeof payload === 'object') {
    const candidate =
      payload.base64 ||
      payload.qrcode?.base64 ||
      payload.qrcode?.code ||
      payload.qrcode ||
      payload.code ||
      payload.value ||
      null;

    return toDataUrl(candidate);
  }

  return null;
};

const candidateHasValue = value =>
  value !== undefined && value !== null && value !== '';

const extractStatusCandidates = payload => {
  if (!payload || typeof payload !== 'object') return [];

  return [
    payload.connection,
    payload.status,
    payload.state,
    payload?.response?.connection,
    payload?.response?.status,
    payload?.response?.state,
    payload?.status?.connection,
    payload?.status?.status,
    payload?.status?.state,
    payload?.instance?.status,
    payload?.instance?.state,
  ];
};

const extractPairCandidates = payload => {
  if (!payload || typeof payload !== 'object') return [];

  return [
    payload.paircode,
    payload.pairCode,
    payload.response?.paircode,
    payload.response?.pairCode,
    payload.instance?.paircode,
    payload.instance?.pairCode,
  ];
};

const extractQrCandidates = payload => {
  if (!payload || typeof payload !== 'object') return [];

  return [
    payload.qr_data_url,
    payload.qrDataUrl,
    payload.qrcode,
    payload.qrCode,
    payload.response?.qr_data_url,
    payload.response?.qrcode,
    payload.response?.qrCode,
    payload.instance?.qr_data_url,
    payload.instance?.qrcode,
    payload.instance?.qrCode,
  ];
};

watch(
  () => props.inbox?.provider_connection,
  providerConnection => {
    const connectionCandidate =
      extractStatusCandidates(providerConnection).find(candidateHasValue) || '';
    rawConnectionStatus.value = connectionCandidate;

    if (isConnectedStatus(connectionCandidate)) {
      qrCode.value = null;
    }

    const providerPair =
      extractPairCandidates(providerConnection).find(candidateHasValue) || '';
    pairCode.value = providerPair
      ? providerPair.toString().trim().toUpperCase()
      : '';

    const providerQr =
      extractQrCandidates(providerConnection).find(candidateHasValue) || '';

    if (providerQr) {
      qrCode.value = providerQr;
    } else if (isConnectedStatus(connectionCandidate)) {
      qrCode.value = null;
    }
  },
  { immediate: true, deep: true }
);

const extractErrorMessage = error => {
  if (!error) return '';

  const errorData = error.response?.data;
  const candidate = errorData?.error ?? errorData;

  if (typeof candidate === 'string') return candidate;

  if (candidate && typeof candidate === 'object') {
    const nestedMessage =
      candidate.message || candidate.error || candidate.response;
    if (typeof nestedMessage === 'string') return nestedMessage;
  }

  return error.message;
};

const handleConnectionMetadata = data => {
  if (!data) return;

  const statusCandidate =
    extractStatusCandidates(data).find(candidateHasValue) || '';
  rawConnectionStatus.value = statusCandidate;

  if (isConnectedStatus(statusCandidate)) {
    qrCode.value = null;
  }

  const candidatePair =
    extractPairCandidates(data).find(candidateHasValue) || '';
  const normalizedPair = candidatePair ? candidatePair.toString().trim() : '';
  pairCode.value = normalizedPair ? normalizedPair.toUpperCase() : '';
};

const fetchInstanceStatus = async ({ silent = true } = {}) => {
  try {
    const response = await InboxesAPI.getUazapiInstanceStatus(props.inbox.id);
    return response.data || {};
  } catch (error) {
    if (!silent) {
      useAlert(
        extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.STATUS.ERROR')
      );
    }
    return null;
  }
};

const applyConnectionPayload = async (
  payload,
  { openModal = false, notify = false } = {}
) => {
  if (!payload || typeof payload !== 'object') return null;

  const nestedPayload =
    payload.provider_connection ||
    payload.connection_data ||
    payload.providerConnection ||
    payload.connectionData;

  const normalizedPayload =
    nestedPayload && typeof nestedPayload === 'object'
      ? { ...payload, ...nestedPayload }
      : payload;

  handleConnectionMetadata(normalizedPayload);

  if (isConnectedStatus(rawConnectionStatus.value)) {
    qrCode.value = null;
    return null;
  }

  if (!openModal) return null;

  const providerQr =
    extractQrCandidates(normalizedPayload).find(candidateHasValue) || '';
  const qr = await normalizeQrPayload(providerQr || normalizedPayload);

  if (qr) {
    qrCode.value = qr;
    if (notify) {
      useAlert(t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.SUCCESS'));
    }
  } else {
    qrCode.value = null;
  }

  return qr;
};

const generateQrCode = async () => {
  try {
    isGeneratingQr.value = true;
    qrCode.value = null;
    const response = await InboxesAPI.connectUazapiInstance(props.inbox.id, {
      phone: '',
    });

    const payload = response.data || {};
    const statusPayload = await fetchInstanceStatus({ silent: true });
    if (statusPayload) {
      await applyConnectionPayload(statusPayload);
    }
    await applyConnectionPayload(payload, { openModal: true, notify: true });
  } catch (error) {
    useAlert(
      extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.ERROR')
    );
  } finally {
    isGeneratingQr.value = false;
  }
};

const disconnectInstance = async () => {
  if (!props.inbox?.id || isDisconnecting.value) return;

  try {
    isDisconnecting.value = true;
    const response = await InboxesAPI.disconnectUazapiInstance(props.inbox.id);
    const payload = response.data || {};

    await applyConnectionPayload(payload);
    const statusPayload = await fetchInstanceStatus({ silent: true });
    await applyConnectionPayload(statusPayload);

    qrCode.value = null;

    useAlert(t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.ERROR')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

const matchesInbox = payload => {
  if (!payload) return false;

  const incoming = payload.inbox_id ?? payload.inboxId;
  if (incoming !== undefined && incoming !== null) {
    return String(incoming) === String(props.inbox?.id);
  }

  const metaId = payload.metadata?.['chatwoot.inbox_id'];
  if (metaId !== undefined && metaId !== null) {
    return String(metaId) === String(props.inbox?.id);
  }

  return false;
};

const onConnectionUpdate = async payload => {
  if (!matchesInbox(payload)) return;

  await applyConnectionPayload(payload, { openModal: false });

  if (isConnectedStatus(rawConnectionStatus.value)) return;

  const statusPayload = await fetchInstanceStatus({ silent: true });
  await applyConnectionPayload(statusPayload);
};

onMounted(async () => {
  emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);

  if (!props.inbox?.id) return;

  const statusPayload = await fetchInstanceStatus({ silent: true });
  await applyConnectionPayload(statusPayload);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
});

watch(
  () => props.inbox?.id,
  async newId => {
    if (!newId) return;

    const statusPayload = await fetchInstanceStatus({ silent: true });
    await applyConnectionPayload(statusPayload);
  }
);
</script>

<template>
  <div class="flex flex-col items-center justify-center py-8">
    <!-- Connection Status -->
    <div class="flex flex-col items-center gap-4 mb-6">
      <div
        class="flex items-center gap-2 px-4 py-2 rounded-full"
        :class="{
          'bg-green-100 dark:bg-green-900/30': isConnected,
          'bg-yellow-100 dark:bg-yellow-900/30':
            !isConnected &&
            ['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
              normalizeStatus(rawConnectionStatus)
            ),
          'bg-red-100 dark:bg-red-900/30':
            !isConnected &&
            !['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
              normalizeStatus(rawConnectionStatus)
            ),
        }"
      >
        <span
          class="w-3 h-3 rounded-full"
          :class="{
            'bg-green-500': isConnected,
            'bg-yellow-500 animate-pulse':
              !isConnected &&
              ['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
                normalizeStatus(rawConnectionStatus)
              ),
            'bg-red-500':
              !isConnected &&
              !['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
                normalizeStatus(rawConnectionStatus)
              ),
          }"
        />
        <span
          class="font-medium"
          :class="{
            'text-green-700 dark:text-green-300': isConnected,
            'text-yellow-700 dark:text-yellow-300':
              !isConnected &&
              ['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
                normalizeStatus(rawConnectionStatus)
              ),
            'text-red-700 dark:text-red-300':
              !isConnected &&
              !['connecting', 'scan_qr_code', 'waiting_qr', 'qrcode'].includes(
                normalizeStatus(rawConnectionStatus)
              ),
          }"
        >
          {{ statusText }}
        </span>
      </div>
    </div>

    <!-- QR Code Display (when not connected) -->
    <div v-if="!isConnected" class="flex flex-col items-center gap-4">
      <!-- QR Code Image -->
      <div
        v-if="qrCode"
        class="p-4 bg-white rounded-xl shadow-lg border border-slate-200 dark:border-slate-700"
      >
        <img
          :src="qrCode"
          alt="WhatsApp QR Code"
          class="w-64 h-64 object-contain"
        />
      </div>

      <!-- Placeholder when no QR -->
      <div
        v-else
        class="w-64 h-64 flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600"
      >
        <fluent-icon icon="search" size="48" class="text-slate-400 mb-2" />
        <p class="text-sm text-slate-500 dark:text-slate-400 text-center px-4">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.CLICK_TO_GENERATE') }}
        </p>
      </div>

      <!-- Pairing Code (if available) -->
      <div
        v-if="pairCode"
        class="flex flex-col items-center gap-2 mt-2 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl"
      >
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.LABEL') }}
        </p>
        <p
          class="text-3xl font-bold tracking-widest text-slate-800 dark:text-slate-200"
        >
          {{ pairCode }}
        </p>
        <p class="text-xs text-slate-500 text-center max-w-xs">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.DESCRIPTION') }}
        </p>
      </div>

      <!-- Instructions -->
      <p
        class="text-sm text-slate-600 dark:text-slate-400 text-center max-w-xs"
      >
        {{ $t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.INSTRUCTIONS') }}
      </p>

      <!-- Action Button -->
      <div class="flex gap-3 mt-2">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.BUTTON')"
          :is-loading="isGeneratingQr"
          @click="generateQrCode"
        />
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="flex flex-col items-center gap-4">
      <div
        class="w-64 h-64 flex flex-col items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-200 dark:border-green-800"
      >
        <fluent-icon
          icon="checkmark-circle"
          size="64"
          class="text-green-500 mb-2"
        />
        <p class="text-lg font-medium text-green-700 dark:text-green-300">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.STATUS.CONNECTED') }}
        </p>
        <p
          class="text-sm text-green-600 dark:text-green-400 text-center px-4 mt-1"
        >
          {{ $t('INBOX_MGMT.ADD.UAZAPI.STATUS.READY') }}
        </p>
      </div>

      <!-- Disconnect Button -->
      <NextButton
        variant="faded"
        color="ruby"
        :label="$t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.BUTTON')"
        :is-loading="isDisconnecting"
        @click="disconnectInstance"
      />
    </div>
  </div>
</template>
